import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Badge, Button, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import { DataTable, type Column } from '../components/DataTable';
import { useTRPC } from '../lib/trpc';
import classes from './EntityPage.module.css';

/**
 * Ключі сутності — вкладка «Ключі» її картки (мокап, рядок 3316).
 *
 * Ядро жило рівно на одному екрані `/core`, спільному для всіх сутностей: щоб побачити ключі
 * конкретної сутності, оператор мусив піти туди й відфільтрувати. Мокап натомість тримає їх
 * усередині картки, і причина видима на самій вкладці: `ai_status` тут — не довідкова
 * колонка, а перелік проблем саме цього бренду (канібалізація, дублі, ключ без ГЕО), і поза
 * карткою його нема з чим зіставити.
 */

interface KeywordRow {
  id: string;
  keyword: string;
  countryCode: string;
  language: string;
  volume: number | null;
  positionDeci: number | null;
  aiStatus: string;
  urlPattern: string | null;
  pageBindingId: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  ok: 'ок',
  new_page: 'нова сторінка',
  duplicate: 'дубль',
  cannibalization: 'канібалізація',
  no_geo: 'без ГЕО',
};

export interface EntityKeywordsProps {
  ownerType: 'brand' | 'game' | 'cluster';
  ownerId: string;
}

export function EntityKeywords({ ownerType, ownerId }: EntityKeywordsProps): React.JSX.Element {
  const trpc = useTRPC();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<string | null>(null);

  const list = useQuery(
    trpc.keywords.list.queryOptions({
      ownerType,
      ownerId,
      pagination: { limit: 100, direction: 'asc' },
    }),
  );

  const summary = useQuery(
    trpc.keywords.summary.queryOptions({ ownerType, ownerId }),
  );

  /**
   * Фільтри тут, а не на `/core`.
   *
   * Вкладка існує саме тому, що `ai_status` поза карткою нема з чим зіставити: «канібалізація»
   * має сенс лише серед ключів одного бренду. Але без фільтра перелік на кілька сотень рядків
   * ховає ті пʼять, заради яких вкладку відкрили — а піти фільтрувати на спільний екран
   * означає знову втратити контекст бренду.
   */
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (list.data?.items ?? []).filter(
      (row) =>
        (needle === '' || row.keyword.toLowerCase().includes(needle)) &&
        (status === null || row.aiStatus === status),
    );
  }, [list.data, query, status]);

  const problemCount = Object.entries(summary.data?.byStatus ?? {})
    .filter(([status]) => status !== 'ok')
    .reduce((sum, [, count]) => sum + count, 0);

  const columns: Column<KeywordRow>[] = [
    { key: 'keyword', header: 'Ключ', render: (row) => <span>{row.keyword}</span> },
    {
      key: 'geo',
      header: 'ГЕО',
      render: (row) => <span className={classes.mono}>{row.countryCode}</span>,
    },
    { key: 'lang', header: 'Мова', render: (row) => <span className={classes.mono}>{row.language}</span> },
    {
      key: 'volume',
      header: 'Частотність',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.volume ?? '—'}</span>,
    },
    {
      key: 'position',
      header: 'Позиція',
      align: 'right',
      /** Зберігається ×10, щоб сортування було точним: 137 — це 13,7. */
      render: (row) => (
        <span className={classes.mono}>
          {row.positionDeci === null ? '—' : (row.positionDeci / 10).toFixed(1)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Стан',
      render: (row) => (
        <Badge
          size="sm"
          color={row.aiStatus === 'ok' ? 'mint' : 'amber'}
          data-testid={`kw-status-${row.keyword}`}
        >
          {STATUS_LABELS[row.aiStatus] ?? row.aiStatus}
        </Badge>
      ),
    },
    {
      key: 'page',
      header: 'Сторінка',
      /**
       * Ключ без привʼязки — робота, яку ще не зробили, а не помилка даних. Привʼязка може
       * бути двох видів: на конкретну сторінку (`pageBindingId`) або на патерн адреси, який
       * ще не матеріалізувався в сторінку.
       */
      render: (row) =>
        row.pageBindingId === null && row.urlPattern === null ? (
          <span className={classes.dim}>не привʼязано</span>
        ) : (
          <span className={classes.mono}>{row.urlPattern ?? 'сторінка'}</span>
        ),
    },
  ];

  return (
    <Stack gap="sm" data-testid={`${ownerType}-keywords`}>
      <Group gap="xs" align="flex-end">
        <TextInput
          label="Пошук"
          placeholder="ключ…"
          size="xs"
          value={query}
          onChange={(event) => {
            setQuery(event.currentTarget.value);
          }}
          data-testid={`${ownerType}-kw-search`}
          style={{ width: 200 }}
        />
        <Select
          label="Стан"
          placeholder="усі"
          data={Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))}
          value={status}
          onChange={setStatus}
          clearable
          size="xs"
          data-testid={`${ownerType}-kw-status-filter`}
          style={{ width: 170 }}
        />
        <Group gap={6} style={{ marginLeft: 'auto' }}>
          {/*
            * Двері на «Ядро та ключі» — з картки, як у мокапі (рядки 3316, 2850). Досі екран
            * імпорту не мав жодного входу з застосунку: рядок сайдбару прибрано за планом, а
            * звідси на нього ніщо не вело, тож дістатись до нього можна було лише набравши
            * адресу руками. Бренд їде параметром, щоб не вибирати його вдруге зі списку.
            */}
          <Button
            size="xs"
            variant="default"
            data-testid={`${ownerType}-kw-import`}
            onClick={() => {
              void navigate({ to: '/core', search: { ownerType, ownerId } });
            }}
          >
            Імпортувати ядро
          </Button>
        </Group>
      </Group>

      {summary.data === undefined ? null : (
        <Group gap="md">
          <Text className={classes.dim} data-testid="kw-total">
            усього: {summary.data.total}
          </Text>
          <Text className={classes.dim} data-testid="kw-unbound">
            без сторінки: {summary.data.unbound}
          </Text>
          {/*
            * Проблемні рахуються з розкладки по статусах, а не окремим полем: `ok` — єдиний
            * стан, який не потребує роботи, тож «усе, що не ok» і є перелік проблем.
            */}
          <Text className={problemCount > 0 ? classes.warn : classes.dim} data-testid="kw-problems">
            проблемних: {problemCount}
          </Text>
        </Group>
      )}

      <DataTable
        rows={rows}
        columns={columns}
        loading={list.isPending}
        error={list.error}
        emptyMessage="Ключів немає — імпортуйте ядро на екрані «Ядро та ключі»."
        rowTestId={(row) => `${ownerType}-kw-${row.keyword}`}
        testId={`${ownerType}-kw-table`}
      />
    </Stack>
  );
}
